import { apiCustomer } from "@/api"
import profileAtom from "@/atoms/profile"
import { showToast } from "@/components/feedback"
import { BottomSheet } from "@/components/modals/BottomSheet"
import { Button, Field, Heading, IconBadge, SHEET_GUTTER, Text, TextField } from "@/components/ui"
import { Confetti } from "@/icons"
import { cn } from "@/lib/cn"
import { getAccountToken } from "@/lib/account-token"
import * as Dialog from "@radix-ui/react-dialog"
import { isAxiosError } from "axios"
import { useAtom } from "jotai"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

/** How long "Agora não" keeps the sheet away. */
export const COMPLETE_EMAIL_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000
const SNOOZE_PREFIX = "meucontroleai:complete-email-snooze:"
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/

function snoozedUntil(profileId: string): number {
  try {
    return Number(localStorage.getItem(SNOOZE_PREFIX + profileId) ?? 0)
  } catch {
    return 0
  }
}

function snooze(profileId: string) {
  try {
    localStorage.setItem(SNOOZE_PREFIX + profileId, String(Date.now() + COMPLETE_EMAIL_SNOOZE_MS))
  } catch {
    return
  }
}

/** Routes where the sheet never interrupts: checkout, the public tracking page and the login page. */
function quietRoute(pathname: string): boolean {
  return pathname.startsWith("/cart/checkout") || pathname.startsWith("/p/") || pathname.startsWith("/profile/auth")
}

/**
 * Onboarding for a customer the WhatsApp assistant created from the phone alone (no email): after login, a friendly
 * sheet asks for the email and optionally the name (`PATCH /me`), skippable with a 3-day "remind later".
 */
export function CompleteEmailSheet() {
  const [profile, setProfile] = useAtom(profileAtom)
  const { pathname } = useLocation()
  const [needsEmail, setNeedsEmail] = useState(false)
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const profileId = profile?.id ?? ""

  useEffect(() => {
    if (!profileId || !getAccountToken()) {
      setNeedsEmail(false)
      return
    }
    let cancelled = false
    apiCustomer
      .get<{ needsEmail?: boolean; name?: string }>("/me")
      .then(({ data }) => {
        if (cancelled) return
        setNeedsEmail(data.needsEmail === true)
        setName(data.name ?? "")
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [profileId])

  useEffect(() => {
    setOpen(needsEmail && !!profileId && !quietRoute(pathname) && snoozedUntil(profileId) < Date.now())
  }, [needsEmail, profileId, pathname])

  function later() {
    if (profileId) snooze(profileId)
    setOpen(false)
  }

  async function save() {
    const value = email.trim()
    if (!EMAIL_PATTERN.test(value)) {
      setError("Confira o e-mail: ele precisa ter o formato nome@exemplo.com.")
      return
    }
    setSaving(true)
    setError(null)
    try {
      const { data } = await apiCustomer.patch<{ name: string }>("/me", { email: value, name: name.trim() })
      if (profile) setProfile({ ...profile, name: data.name || profile.name })
      setNeedsEmail(false)
      setOpen(false)
      showToast.success("Cadastro completo! Obrigado.")
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : undefined
      setError(
        status === 409
          ? "Este e-mail já está em uso em outra conta. Use outro e-mail."
          : status === 400
            ? "Confira o e-mail: ele precisa ter o formato nome@exemplo.com."
            : "Não deu pra salvar agora. Tente de novo em instantes.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <BottomSheet open={open} onOpenChange={(next) => (next ? setOpen(true) : later())}>
      <div className={cn("mx-auto flex w-full max-w-md flex-col pb-safe pt-2", SHEET_GUTTER)}>
        <IconBadge icon={Confetti} tone="primary" size="lg" weight="duotone" className="mb-3" />
        <Dialog.Title asChild>
          <Heading level={2} size="title" leading="snug">
            Você já fez pedido com a gente pelo WhatsApp
          </Heading>
        </Dialog.Title>
        <Text tone="muted" className="mt-1">
          Complete seu cadastro com seu e-mail para receber seus pedidos e novidades da loja.
        </Text>

        <form
          className="mt-5 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            void save()
          }}
          noValidate
        >
          <Field label="E-mail" error={error}>
            <TextField
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="nome@exemplo.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setError(null)
              }}
            />
          </Field>
          <Field
            label={
              <>
                Nome <Text as="span" weight="normal" tone="subtle">(opcional)</Text>
              </>
            }
          >
            <TextField autoComplete="name" placeholder="Como podemos te chamar?" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <div className="mt-1 flex flex-col gap-2">
            <Button type="submit" size="lg" fullWidth isLoading={saving} data-testid="complete-email-save">
              {saving ? "Salvando…" : "Completar cadastro"}
            </Button>
            <Button type="button" variant="ghost" tone="neutral" size="md" fullWidth onClick={later}>
              Agora não, me lembre depois
            </Button>
          </div>
        </form>
      </div>
    </BottomSheet>
  )
}
